import { DEVICES, DEVICE_TYPE, MedconnectDeviceValue } from '@/constants/medconnect-devices-types'

/** How a device is physically reached; drives which wizard step fields are shown. */
export type DeviceConnectionKind = 'bluetooth' | 'serial' | 'folder' | 'program'

export type DeviceSettingsFieldName = Exclude<keyof MedconnectDeviceValue, 'name' | 'type' | 'ble_id' | 'port' | 'baudrate'>

export type DeviceSettingsField = {
  name: DeviceSettingsFieldName
  required: boolean
  /** Translation key suffix under `page.configuration.MedconnectDevice.wizard.fields`. */
  labelKey: string
  defaultValue?: string
  options?: readonly string[]
}

export type DeviceSettingsSpec = {
  connection: DeviceConnectionKind
  type: DEVICE_TYPE
  fields: readonly DeviceSettingsField[]
  /** Baud rate applied to serial devices; kept out of the UI on purpose. */
  baudrate?: 9600 | 115200
}

const SERIAL_SPEC: DeviceSettingsSpec = { connection: 'serial', type: DEVICE_TYPE.SERIAL, fields: [], baudrate: 9600 }
const BLUETOOTH_SPEC: DeviceSettingsSpec = { connection: 'bluetooth', type: DEVICE_TYPE.BLE, fields: [] }

/**
 * Per-device description of what the guided setup must collect. Everything a
 * technician used to type into the flat form is either derived (type, baud
 * rate), discovered (Bluetooth unit, serial port) or asked here with a hint.
 */
export const DEVICE_SETTINGS_SPECS: Readonly<Record<DEVICES, DeviceSettingsSpec>> = {
  [DEVICES.PC303]: SERIAL_SPEC,
  [DEVICES.PM6100]: SERIAL_SPEC,
  [DEVICES.WELCH_ALLYN_CONNEX_SPOT_MONITOR]: SERIAL_SPEC,
  [DEVICES.IM3S]: SERIAL_SPEC,
  [DEVICES.F4]: BLUETOOTH_SPEC,
  [DEVICES.THERMO_TAIDOC]: BLUETOOTH_SPEC,
  [DEVICES.TAIDOC_BGMETER]: BLUETOOTH_SPEC,
  [DEVICES.PM6100_BLE]: BLUETOOTH_SPEC,
  [DEVICES.HDSTETH]: BLUETOOTH_SPEC,
  [DEVICES.IM3S_BLE]: {
    connection: 'bluetooth',
    type: DEVICE_TYPE.BLE,
    fields: [{ name: 'ble_name', required: false, labelKey: 'bleName', defaultValue: 'iM3' }],
  },
  [DEVICES.SCHILLER_ECG]: {
    connection: 'folder',
    type: DEVICE_TYPE.SERIAL,
    fields: [
      { name: 'gdt_folder_path', required: true, labelKey: 'gdtFolderPath', defaultValue: 'C:\\schiller' },
      { name: 'receiver_name', required: true, labelKey: 'receiverName', defaultValue: 'EMR1' },
      { name: 'sender_name', required: true, labelKey: 'senderName', defaultValue: 'SCH1' },
      { name: 'ecg_program_path', required: false, labelKey: 'ecgProgramPath', defaultValue: 'C:\\Program Files (x86)\\CS-104' },
    ],
  },
  [DEVICES.ECG_WATCH_FOLDER]: {
    connection: 'folder',
    type: DEVICE_TYPE.SERIAL,
    fields: [
      { name: 'ecg_folder_path', required: true, labelKey: 'ecgFolderPath' },
      { name: 'filePrefix', required: false, labelKey: 'filePrefix' },
      { name: 'fileSuffix', required: false, labelKey: 'fileSuffix' },
    ],
  },
  [DEVICES.SE_1515]: {
    connection: 'program',
    type: DEVICE_TYPE.SERIAL,
    fields: [{ name: 'ecg_program_path', required: true, labelKey: 'ecgProgramPath' }],
  },
  [DEVICES.TOUCH_ECG]: {
    connection: 'program',
    type: DEVICE_TYPE.SERIAL,
    fields: [{ name: 'ecg_program_path', required: true, labelKey: 'ecgProgramPath' }],
  },
  [DEVICES.MIR_SPIROMETRY]: {
    connection: 'program',
    type: DEVICE_TYPE.SERIAL,
    fields: [
      { name: 'mir_software_type', required: true, labelKey: 'mirSoftwareType', defaultValue: 'MIRSpiro', options: ['MIRSpiro', 'winspiroPRO', 'spiroConnect'] },
      { name: 'mir_protocol', required: true, labelKey: 'mirProtocol', defaultValue: 'HL7', options: ['HL7', 'EXCHANGE'] },
      { name: 'mir_exchange_folder', required: true, labelKey: 'mirExchangeFolder', defaultValue: 'C:\\MIR\\MirSpiro\\HL7' },
      { name: 'mir_exchange_folder_exchange', required: false, labelKey: 'mirExchangeFolderExchange', defaultValue: 'C:\\MIR\\MirSpiro' },
      { name: 'mir_program_path', required: false, labelKey: 'mirProgramPath', defaultValue: 'C:\\Program Files (x86)\\MIR srl Medical International Research\\MIR Spiro' },
    ],
  },
}

export function getDeviceSettingsSpec(device: DEVICES): DeviceSettingsSpec {
  return DEVICE_SETTINGS_SPECS[device]
}

/** Initial draft for a device, with the spec's defaults applied. */
export function buildDeviceDraft(device: DEVICES): MedconnectDeviceValue {
  const spec = getDeviceSettingsSpec(device)
  const draft: MedconnectDeviceValue = { name: device, type: spec.type }
  if (spec.baudrate) draft.baudrate = spec.baudrate
  for (const field of spec.fields) {
    if (field.defaultValue !== undefined) draft[field.name] = field.defaultValue
  }
  return draft
}

/** Names of the fields still missing before a draft can be saved. */
export function getMissingRequiredFields(draft: MedconnectDeviceValue): string[] {
  const spec = getDeviceSettingsSpec(draft.name)
  const missing: string[] = spec.fields.filter((field) => field.required && !draft[field.name]).map((field) => field.name)
  if (spec.connection === 'serial' && !draft.port) missing.push('port')
  return missing
}
